import React, { useState, useEffect } from 'react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { FaChartLine, FaCalendarAlt, FaRocket, FaExclamationTriangle, FaCheckCircle, FaUsers } from 'react-icons/fa';
import "./planui.css";

const STRANDS_API_URL = process.env.REACT_APP_STRANDS_API_URL || 'http://localhost:5001';

const Plan = () => {
    const { user } = useAuthenticator((context) => [context.user]);
    const [usage, setUsage] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchUsage = async () => {
        if (!user?.username) return;

        try {
            setIsLoading(true);
            setError(null);

            const response = await fetch(
                `${STRANDS_API_URL}/api/user/usage?username=${encodeURIComponent(user.username)}`
            );

            if (!response.ok) {
                throw new Error('Error al obtener datos de uso');
            }

            const data = await response.json();

            if (data.success) {
                setUsage(data.usage);
            } else {
                throw new Error(data.error || 'Error desconocido');
            }
        } catch (err) {
            console.error('[Plan] Error:', err);
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchUsage();
    }, [user?.username]);

    // Formatear fecha
    const formatResetDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-MX', {
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        });
    };

    // Obtener el mes actual
    const getCurrentMonth = () => {
        const now = new Date();
        return now.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
    };

    // Determinar color de la barra según el estado
    const getProgressColor = (status) => {
        switch (status) {
            case 'exceeded':
                return '#dc2626'; // Rojo
            case 'warning':
                return '#f59e0b'; // Amarillo
            default:
                return '#10b981'; // Verde
        }
    };

    // Obtener icono según estado
    const getStatusIcon = (status) => {
        switch (status) {
            case 'exceeded':
                return <FaExclamationTriangle className="status-icon exceeded" />;
            case 'warning':
                return <FaExclamationTriangle className="status-icon warning" />;
            default:
                return <FaCheckCircle className="status-icon ok" />;
        }
    };

    // Obtener mensaje según estado
    const getStatusMessage = (status, remaining) => {
        switch (status) {
            case 'exceeded':
                return 'La organización ha excedido el límite mensual';
            case 'warning':
                return `Quedan ${remaining} consultas para la organización`;
            default:
                return `Quedan ${remaining} consultas para la organización`;
        }
    };

    if (isLoading) {
        return (
            <div className="plan-container">
                <div className="plan-loading">
                    <div className="plan-spinner"></div>
                    <p>Cargando información del plan...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="plan-container">
                <div className="plan-error">
                    <FaExclamationTriangle className="error-icon" />
                    <p>{error}</p>
                    <button onClick={fetchUsage} className="retry-btn">
                        Reintentar
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="plan-container">
            {/* Header */}
            <div className="plan-header">
                <div className="plan-title">
                    <FaRocket className="plan-icon" />
                    <h1>Mi Plan</h1>
                </div>
            </div>

            {/* Card Principal de Uso */}
            <div className={`usage-card ${usage?.status || 'ok'}`}>
                <div className="usage-header">
                    <div className="usage-title">
                        <FaChartLine className="usage-icon" />
                        <span>Consultas del Mes (Organización)</span>
                    </div>
                    {getStatusIcon(usage?.status)}
                </div>

                {/* Números grandes */}
                <div className="usage-numbers">
                    <span className="current-count">{usage?.monthly_count || 0}</span>
                    <span className="separator">/</span>
                    <span className="limit-count">{usage?.monthly_limit || 1000}</span>
                </div>

                {/* Barra de progreso */}
                <div className="progress-container">
                    <div
                        className="progress-bar"
                        style={{
                            width: `${Math.min(usage?.percentage || 0, 100)}%`,
                            backgroundColor: getProgressColor(usage?.status)
                        }}
                    />
                </div>

                {/* Porcentaje */}
                <div className="usage-percentage">
                    {usage?.actual_percentage?.toFixed(1) || 0}% utilizado
                </div>

                {/* Mensaje de estado */}
                <div className={`status-message ${usage?.status || 'ok'}`}>
                    {getStatusMessage(usage?.status, usage?.remaining)}
                </div>
            </div>

            {/* Info adicional */}
            <div className="plan-info-grid">
                <div className="info-card">
                    <div className="info-icon">
                        <FaCalendarAlt />
                    </div>
                    <div className="info-content">
                        <span className="info-label">Período</span>
                        <span className="info-value">{getCurrentMonth()}</span>
                    </div>
                </div>

                <div className="info-card">
                    <div className="info-icon">
                        <FaChartLine />
                    </div>
                    <div className="info-content">
                        <span className="info-label">Restantes</span>
                        <span className="info-value">{usage?.remaining || 0} consultas</span>
                    </div>
                </div>

                <div className="info-card">
                    <div className="info-icon">
                        <FaUsers />
                    </div>
                    <div className="info-content">
                        <span className="info-label">Usuarios</span>
                        <span className="info-value">{usage?.organization_users || 1} en la org.</span>
                    </div>
                </div>
            </div>

            {/* Detalles del plan */}
            <div className="plan-details">
                <h3>Detalles del Plan</h3>
                <div className="details-list">
                    <div className="detail-row">
                        <span className="detail-label">Usuarios en la organización</span>
                        <span className="detail-value">{usage?.organization_users || 1}</span>
                    </div>
                    <div className="detail-row">
                        <span className="detail-label">Límite mensual</span>
                        <span className="detail-value">{usage?.monthly_limit || 1000} consultas</span>
                    </div>
                    <div className="detail-row">
                        <span className="detail-label">Consultas usadas este mes</span>
                        <span className="detail-value">{usage?.monthly_count || 0} consultas</span>
                    </div>
                </div>
            </div>

            {/* Nota de reinicio */}
            <div className="plan-note">
                <p>El contador muestra las consultas de TODOS los usuarios de la organización. Se reinicia el primer día de cada mes.</p>
            </div>
        </div>
    );
};

export default Plan;
